import {inject, Injectable} from '@angular/core';
import {TelegramApiService} from "./telegram-api.service";
import {
  AccountQueryParams,
  ChatsQueryParams,
  CreateChatQueryParams,
  GetFileParams,
  MessagesQueryParams,
  SendDocumentQueryParams,
  SendImageQueryParams,
  SendMessageQueryParams,
  TelegramAccount,
  UserChatQueryParams
} from "../types/telegram-page.types";
import {ChatTelegram} from "../types/chat-telegram.types";
import {UserTelegram} from "../types/user-telegram.types";
import {MessageTelegram} from "../types/last-message.types";
import {firstValueFrom} from "rxjs";
import {AttachChatRecordCalenderIdParams} from "../types/attach-chat-record-calender";

@Injectable({
  providedIn: 'root'
})
export class TelegramService {
  private telegramApiService: TelegramApiService = inject(TelegramApiService);

  public getMainChats(chatsQueryParams: ChatsQueryParams): Promise<Array<ChatTelegram>> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.getMainChats(chatsQueryParams).subscribe({
        next: (chats: Array<ChatTelegram>) => resolve(chats),
        error: (error) => reject(error)
      });
    });
  }

  public getAccounts(): Promise<Array<TelegramAccount>> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.getAccounts().subscribe({
        next: (accounts: Array<TelegramAccount>) => resolve(accounts),
        error: (error) => reject(error)
      });
    });
  }

  public getArchiveChats(chatsQueryParams: ChatsQueryParams): Promise<Array<ChatTelegram>> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.getArchiveChat(chatsQueryParams).subscribe({
        next: (chats: Array<ChatTelegram>) => resolve(chats),
        error: (error) => reject(error)
      });
    });
  }

  public getAccountInfo(accountQueryParams: AccountQueryParams): Promise<UserTelegram> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.getAccount(accountQueryParams).subscribe({
        next: (account: UserTelegram) => resolve(account),
        error: (error) => reject(error)
      });
    });
  }

  public getMessages(getMessagesQueryParams: MessagesQueryParams): Promise<Array<MessageTelegram>> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.getMessages(getMessagesQueryParams).subscribe({
        next: (messages: Array<MessageTelegram>) => resolve(messages),
        error: (error) => reject(error)
      });
    });
  }

  public getUserChat(params: UserChatQueryParams): Promise<number> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.getUserChat(params).subscribe({
        next: (chatId: number) => resolve(chatId),
        error: (error) => reject(error)
      });
    });
  }

  public sendMessage(sendMessageQueryParams: SendMessageQueryParams): Promise<void> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.sendMessage(sendMessageQueryParams).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public sendImageMessage(sendImageQueryParams: SendImageQueryParams): Promise<void> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.sendImageMessage(sendImageQueryParams).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public sendDocumentMessage(sendDocumentQueryParams: SendDocumentQueryParams): Promise<void> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.sendDocumentMessage(sendDocumentQueryParams).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public createChat(createChatQueryParams: CreateChatQueryParams): Promise<number> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.createChat(createChatQueryParams).subscribe({
        next: (chatId: number) => resolve(chatId),
        error: (error) => reject(error)
      });
    });
  }

  public fetchImageUrl(getFileParams: GetFileParams): Promise<string> {
    const observable = this.telegramApiService.getImage(getFileParams);

    return firstValueFrom(observable);
  }

  public attachChatRecordCalenderId(attachChatRecordCalenderIdParams: AttachChatRecordCalenderIdParams): Promise<void> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.attachChatRecordCalenderId(attachChatRecordCalenderIdParams).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }
}
